import React, { useEffect, useState } from 'react';
import { Route, RouteComponentProps, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import { useApp } from '../../contexts/app/app.context';
import { FooterComponent } from '../../components/common/footer/footer.component';
import { Option } from 'fp-ts/lib/Option';
import { TUser } from '../../models/user.model';
import { GetStartedContainer } from '../get-started/get-started.container';
import AboutYouContainer from '../../components/aboutyou/aboutyou.container';
import { RemoteData } from '@assetmark/ui-kit/dist/utils/remote-data.utils';
import { TApplicationState } from '../../models/application-state';
import { useLead } from '../../contexts/lead/lead.context';
import { useGoal } from '../../contexts/goal/goal.context';
import { TLeadStrategyInfoResponse } from '../../models/lead-strategy-info-response';
import { convertLastPageToRedirectRoute, parseGoalTypeEnum } from '../../helpers/model.helper';
import GoalsContainer from '../goals/goals.container';
import { TLeadGoalsResponse } from '../../models/goals-info-response';
import { TAddUpdateLeadGoal } from '../../models/add-update-lead-goal';
import { ErrorPage } from '../errorPage/errorPage.component';
import { LeadApi } from '../../apis/lead.api';
import { LeadCategory } from '../../models/lead-category-enum';
import { RiskProfileContainer } from '../risk-profile/risk-profile.container';
import MenuBarContainer from '../../components/common/menuBar/menuBar.container';
import { YourAnalysisContainer } from '../../modules/analysis/analysis.container';
import { clearLocalData } from '../../helpers/storage.helper';
import FullPageLoader from '../../components/common/full-page-loader/fullPageLoader';
import SpinnerContainer from '../spinner/spinner.container';
// import { Theme } from '@assetmark/ui-kit/dist/components/theme/theme.component';

export type TAppProps = {
	isApiError: boolean;
	user: Option<TUser>;
	applicationState: RemoteData<TApplicationState>;
	redirectToPage: string;
	savedLeadStrategiesData: RemoteData<TLeadStrategyInfoResponse>;
	savedGoalsData: () => void;
	savedLeadGoalsData: RemoteData<TLeadGoalsResponse>;
} & RouteComponentProps;

const App: React.FC<TAppProps> = ({ isApiError, user, applicationState, redirectToPage, savedLeadStrategiesData, savedGoalsData, savedLeadGoalsData }) => {
	const history = useHistory();
	const { isAppLoaded, loadUser, loadApplicationState, showSpinner, setShowSpinner } = useApp();
	const { leadProfile, updateAboutYouState } = useLead();
	const { updateGoalData } = useGoal();
	const [isActive, setActive] = useState<boolean | null>(null);
	
	useEffect(() => {
		user.map(user => {
			loadUser(user);
			setActive(user.roles[0].isActive.getOrElse(false));
		});
	}, [user]);

	useEffect(() => {
		applicationState.isSuccess() && loadApplicationState(applicationState.value);
	}, [applicationState]);

	useEffect(() => {
		if (savedLeadGoalsData.isSuccess()) {
			updateGoalData(savedLeadGoalsData.value);
		} else {
			// console.log('Start fresh goal journey.');
		}
	},[savedLeadGoalsData]);

	useEffect(() => {
		console.log(`savedLeadStrategiesData: `, savedLeadStrategiesData);
		// if leadid present in localstorage then fetch saved data from api and route user to last visited page
		if (savedLeadStrategiesData.isSuccess()) {
			updateAboutYouState(savedLeadStrategiesData.value);
			// redirect to last page visited.
			savedLeadStrategiesData.value.lastPageVisited.fold(history.push(`/start/about`), lastPage => {
				if (lastPage.toLowerCase() === LeadCategory.ClientProfile.toLowerCase()) {
					history.push(`/start/about`);
				} else if (lastPage.toLowerCase() === LeadCategory.SetGoal.toLowerCase()) {
					const goalType = savedLeadStrategiesData.value.goalType
						.map(goalType => parseGoalTypeEnum(`${goalType}`))
						.getOrElse('0');
					if (goalType === '3') {
						history.push(`/start/goals/investment_target`);
					} else if (goalType === '2') {
						history.push(`/start/goals/retirement_age`);
					} else if (goalType === '1') {
						history.push(`/start/risk-profile/investment_return`);
					} else {
						history.push(`/start/about`);
					}
				} else if (lastPage.toLowerCase() === LeadCategory.RiskProfile.toLowerCase()) {
					history.push(`/start/risk-profile/investment_return`);
				} else if (lastPage.toLowerCase() === LeadCategory.YourAnalysis.toLowerCase()) {
					history.push(`/start/your-analysis`);
				} else {
					history.push(`/start/about`);
				}
			});
		} else if (savedLeadStrategiesData.isFailure())  {
			// clean local storage data and move to get started page
			clearLocalData();
			history.push(`/`); 
		} else {
			// console.log('Start fresh lead journey.');
			//clearLocalData();
			history.push(`/`); // send back to get started page
		}
	}, [savedLeadStrategiesData]);

	useEffect(() => {
		if(redirectToPage) {
			history.push(`/start/${redirectToPage}`);
		}
	}, [redirectToPage]);

	if(isApiError) {
		//setShowSpinner(false);
		return <ErrorPage history={history}/>;
	}

	if (!isAppLoaded && isActive === null) {
		return <FullPageLoader />;
	}

	if (!isActive) {
		return <ErrorPage history={history}/>;
	}
	return (
		<>
			<Switch>
				<Route exact path="/">
					<GetStartedContainer />
				</Route>
				<Route path="/start">
					<Start/>
				</Route>
			</Switch>
			<SpinnerContainer />
		</>
	);
};

function Start() {
	const { path } = useRouteMatch();
	useEffect(() => { // handle browser back event here.
		const onBackButtonEvent = (e: any) => {
			e.preventDefault();
			if(window.location.pathname.includes('about')) {
				LeadApi.redirectToPage$.next("about");
			}
			if(window.location.pathname.includes('goals/retirement_age')) {
				LeadApi.redirectToPage$.next("goals/retirement_age");
			}
			if(window.location.pathname.includes('goals/monthly_income')) {
				LeadApi.redirectToPage$.next("goals/monthly_income");
			}
			if(window.location.pathname.includes('goals/retirement_income')) {
				LeadApi.redirectToPage$.next("goals/retirement_income");
			}
			if(window.location.pathname.includes('goals/annual_contribution')) {
				LeadApi.redirectToPage$.next("goals/annual_contribution");
			}
			if(window.location.pathname.includes('goals/investment_target')) {
				LeadApi.redirectToPage$.next("goals/investment_target");
			}
			if(window.location.pathname.includes('risk-profile/investment_return')) {
				LeadApi.redirectToPage$.next("risk-profile/investment_return");
			}
			if(window.location.pathname.includes('risk-profile/maximum_decline')) {
				LeadApi.redirectToPage$.next("risk-profile/maximum_decline");
			}
			if(window.location.pathname.includes('risk-profile/stock_market')) {
				LeadApi.redirectToPage$.next("risk-profile/stock_market");
			}
			if(window.location.pathname.includes('risk-profile/age_range')) {
				LeadApi.redirectToPage$.next("risk-profile/age_range");
			}
			if(window.location.pathname.includes('risk-profile/planned_withdrawal')) {
				LeadApi.redirectToPage$.next("risk-profile/planned_withdrawal");
			}
			if(window.location.pathname.includes('your-analysis')) {
				LeadApi.redirectToPage$.next("your-analysis");
			}
		};
		// window.history.pushState(null, null, window.location.pathname);
		window.addEventListener('popstate', onBackButtonEvent, false);
		return () => {
		  window.removeEventListener('popstate', onBackButtonEvent);  
		};
	  }, []);

	return (
		<div className={'main-container'}>
			<MenuBarContainer />
			<main className={''}>
				<Switch>
					<Route path={`${path}/goals/:subModule?`} component={GoalsContainer} />
					<Route path={`${path}/risk-profile/:subModule`} component={RiskProfileContainer}/>
					<Route path={`${path}/your-analysis`} component={YourAnalysisContainer} />
					{/* <Route path={`${path}/your-analysis`} exact
						render={() => (
							<div className={'text-indigo-700 font-bold sm:mt-3.5'}>
								<div className={'text-indigo-700 font-bold h-60'}></div>
							</div>
						)}/> */}
					<Route path={`${path}/:subModule`} component={AboutYouContainer} />
				</Switch>
				
			</main>
			<FooterComponent />
		</div>
	);
}
export default App;
